import * as React from 'react';
import { useState, useEffect, useContext } from 'react';
import { CloudCheckIcon, CloudIcon, CloudWarningIcon, CloudArrowDownIcon, WindowsLogoIcon, PackageIcon, GoogleDriveLogoIcon } from '@phosphor-icons/react';
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/popover";
import { FetchWithVersionCheck, CaptureError } from '@/shared/functions';
import { recordStat } from '@/shared/functions';
import { StatsContext, FeaturesContext, ToastContext, AuthContext } from '@/app/contexts';
import { RemoteStorage } from '@/shared/types';
import { ToastLevel } from './toast';
import { useSyncStatus, SyncStatusIndicator } from './sync-status';
import { isStatus } from '../question-bar';
const QUEUE_NAME_MAP = {
  BULK_DATAPREP_DISPATCHER_QUEUE_URL: 'Processing Queue'
};
export interface BacklogFromApi {
  processingQueues: {
    [key: string]: number;
  };
}
export interface SimpleStatusCounts {
  IN_PROGRESS: number;
  COMPLETED: number;
  FAILED: number;
}
export interface BacklogProps {
  backlogData: BacklogFromApi;
  provenanceStatusCounts: {
    [key: string]: number;
  };
  userId: string;
  caseId: string | undefined;
  remoteStorageLink: RemoteStorage | null;
}
const Backlog = (props: BacklogProps) => {
  const {
    backlogData,
    provenanceStatusCounts,
    userId,
    caseId,
    remoteStorageLink
  } = props;
  const [isOpen, setOpen] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [totalProcessingQueueMessages, setTotalProcessingQueueMessages] = useState(0);
  const [lastSyncedTime, setLastSyncedTime] = useState<string | null>(null);
  const [processingQueueData, setProcessingQueueData] = useState<{
    [key: string]: number;
  } | undefined>(undefined);
  const [sharepointLinkLocal, setSharepointLinkLocal] = useState<string | null>(null);
  const [simpleStatusCounts, setSimpleStatusCounts] = useState<SimpleStatusCounts>({
    IN_PROGRESS: 0,
    COMPLETED: 0,
    FAILED: 0
  });
  const {
    featureFlags
  } = useContext(FeaturesContext) || {};
  const statsContext = useContext(StatsContext);
  const {
    setToast,
    setToastOpen
  } = useContext(ToastContext);
  const {
    idToken,
    accessToken,
    tenantName
  } = useContext(AuthContext);
  const {
    syncStatus
  } = useSyncStatus(!!featureFlags?.syncStatus);
  useEffect(() => {
    if (provenanceStatusCounts) {
      const newSimpleStatusCounts: SimpleStatusCounts = {
        IN_PROGRESS: 0,
        COMPLETED: 0,
        FAILED: 0
      };
      Object.entries(provenanceStatusCounts).forEach(([status, count]) => {
        if (isStatus(status, 'INTERMEDIARY')) {
          newSimpleStatusCounts.IN_PROGRESS += count;
        } else if (isStatus(status, 'SUCCESS')) {
          newSimpleStatusCounts.COMPLETED += count;
        } else if (isStatus(status, 'ERROR')) {
          newSimpleStatusCounts.FAILED += count;
        }
      });
      setSimpleStatusCounts(newSimpleStatusCounts);
    }
  }, [provenanceStatusCounts]);
  const handleSyncData = async () => {
    setIsSyncing(true);
    try {
      const result = await FetchWithVersionCheck('/api/protected/sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          userId,
          selectedCase: caseId
        })
      });
      if (result.ok) {
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 15000,
          title: 'Success',
          description: 'Data sync initiated successfully. Your new data will be available in a few hours. If you have any questions, please contact your TimePilot CSM.',
          level: ToastLevel.SUCCESS,
          actionButtonText: 'Contact CSM',
          actionButtonOnClick: () => {
            window.open('mailto:csm@tranquility-ai.com', '_blank');
          }
        });
      } else {
        const errorMessage = `Failed to sync data: ${result.statusText}`;
        CaptureError(errorMessage, new Error(errorMessage), !!featureFlags?.useIntercom);
        setToast({
          setOpen: setToastOpen,
          open: true,
          location: 'bottom-right',
          time: Date.now(),
          duration: 15000,
          title: 'Error',
          description: errorMessage,
          level: ToastLevel.ERROR
        });
      }
    } catch (error) {
      const errorMessage = `Error syncing data for user ${userId}: ${error}`;
      CaptureError(errorMessage, error as Error, !!featureFlags?.useIntercom);
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 15000,
        title: 'Error',
        description: 'An error occurred while syncing data. Please try again.',
        level: ToastLevel.ERROR
      });
    } finally {
      setIsSyncing(false);
    }
  };
  useEffect(() => {
    if (backlogData?.processingQueues) {
      const processingQueueDataIn = backlogData.processingQueues;
      setProcessingQueueData(processingQueueDataIn);
      const totalProcessingQueueMessagesIn = processingQueueDataIn ? Object.values(processingQueueDataIn).reduce((acc, curr) => acc + curr, 0) : 0;
      setTotalProcessingQueueMessages(totalProcessingQueueMessagesIn);
    }
    if (syncStatus?.length) {
      const latestUnixSeconds = syncStatus.reduce((latest, row) => {
        const rowLastSync = Math.max(row.lastSyncEnd ?? 0, row.lastFileCopiedTime ?? 0);
        return Math.max(latest, rowLastSync);
      }, 0);
      if (latestUnixSeconds > 0) {
        const latestDate = new Date(latestUnixSeconds * 1000);
        const today = new Date().toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
        const latestDateStr = latestDate.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric'
        });
        const latestTimeStr = latestDate.toLocaleTimeString('en-US', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
        setLastSyncedTime(latestDateStr === today ? latestTimeStr : `${latestDateStr} ${latestTimeStr}`);
      } else {
        setLastSyncedTime(null);
      }
    } else {
      setLastSyncedTime(null);
    }
  }, [backlogData, syncStatus]);
  return <div data-sentry-component="Backlog" data-sentry-source-file="backlog.tsx">
      <Popover id="BacklogPopoverRead" onOpenChange={open => {
      setOpen(open);
      if (open) {
        recordStat('BacklogPopoverRead', new MouseEvent('hover') as unknown as React.MouseEvent, statsContext, !!featureFlags?.useIntercom);
      }
    }} placement={featureFlags?.enableMediaAndSourceShare ? "top-start" : "bottom-end"} showArrow={true} triggerScaleOnOpen={false} classNames={{
      content: 'flex flex-col justify-start items-start bg-white text-black p-4 rounded-md'
    }} data-sentry-element="Popover" data-sentry-source-file="backlog.tsx">
        <PopoverTrigger data-sentry-element="PopoverTrigger" data-sentry-source-file="backlog.tsx">
          <button type="button" id="BacklogPopover" aria-label={lastSyncedTime ? `Backlog - Last Synced: ${lastSyncedTime}` : "Backlog"} aria-haspopup="dialog" className="flex flex-row items-center gap-2 bg-transparent border-none p-0" onMouseEnter={event => recordStat('BacklogPopoverClicked', event, statsContext, !!featureFlags?.useIntercom)}>
            <div className="h-full flex flex-row items-center justify-start px-2 py-[2px] relative gap-2 border-solid border-white/20 hover:border-white/40 hover:cursor-pointer border-[1px] rounded-full opacity-60 hover:opacity-100">
              {featureFlags?.syncStatus ? <SyncStatusIndicator syncStatus={syncStatus} iconSize={20} fallback={<CloudCheckIcon size={22} weight="duotone" className="w-6 h-6" />} /> : <CloudCheckIcon size={22} weight="duotone" className="w-6 h-6" />}
              {lastSyncedTime && <div className="flex flex-row items-center justify-start gap-2">
                  <span>Last Synced (Config):</span>
                  <span>{lastSyncedTime}</span>
                </div>}
            </div>
          </button>
        </PopoverTrigger>
        <PopoverContent data-sentry-element="PopoverContent" data-sentry-source-file="backlog.tsx">
          <div className="flex flex-col justify-between gap-2 bg-white/10 p-2 rounded-md text-gray-950">
            {featureFlags?.backlog && <>
                {(simpleStatusCounts.IN_PROGRESS > 0 || simpleStatusCounts.COMPLETED > 0 || simpleStatusCounts.FAILED > 0) && <>
                    <div className="flex flex-row items-center justify-between gap-2">
                      <div className="text-[16px] font-semibold">File Status</div>
                      <div className="flex flex-row items-center gap-2">
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudCheckIcon size={22} weight="duotone" className="w-6 h-6 text-green-500" />
                          <div className="text-[12px] font-semibold text-gray-950">{simpleStatusCounts.COMPLETED || 0}</div>
                        </div>
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudIcon size={22} weight="duotone" className="w-6 h-6 text-yellow-500" />
                          <div className="text-[12px] font-semibold text-gray-950">{simpleStatusCounts.IN_PROGRESS || 0}</div>
                        </div>
                        <div className="p-1 border border-solid border-black/60 rounded-full flex items-center justify-center gap-1">
                          <CloudWarningIcon size={22} weight="duotone" className="w-6 h-6 text-red-500" />
                          <div className="text-[12px] font-semibold text-gray-950">{simpleStatusCounts.FAILED || 0}</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-row items-center justify-start gap-2 bg-gray-950 h-[2px] w-full">
                    </div>
                  </>}
                <div className="flex flex-row items-center justify-left gap-2 w-full">
                  <div className="flex flex-col justify-left gap-2">
                    <div className="text-[14px] font-semibold">Processing Queue Status</div>
                      {processingQueueData && <div className="flex flex-col justify-left gap-2 bg-white/10 pl-1 rounded-md text-gray-950">
                          {Object.entries(backlogData.processingQueues).map(([queueUrl, queue]) => <div key={queueUrl} className="flex flex-row items-center justify-left gap-2">
                              <div className="text-[12px] font-semibold">
                                {QUEUE_NAME_MAP[queueUrl as keyof typeof QUEUE_NAME_MAP]}
                              </div>
                              <div className="text-[12px]">
                                {queue} files
                              </div>
                            </div>)}
                        </div>}
                  </div>
                </div>
              </>}
            <div className="flex flex-row items-center justify-between gap-2">
              <div className="flex flex-row items-center justify-start">
                {!!remoteStorageLink?.url && remoteStorageLink?.url.trim() !== '' && <a className="flex flex-row items-center justify-center gap-2 px-2 py-1 border border-solid border-black/80 hover:border-black/40 hover:cursor-pointer rounded-full opacity-60 hover:opacity-100 bg-white/10 no-underline text-black/80 hover:text-black/100" id="Sharepoint Link" target="_blank" rel="noopener noreferrer" href={remoteStorageLink?.url as string}>
                    {remoteStorageLink?.type === 'Google Drive' ? <>
                        <GoogleDriveLogoIcon size={22} />
                        <label className="cursor-pointer">Google Drive</label>
                      </> : remoteStorageLink?.type === 'SharePoint' ? <>
                        <WindowsLogoIcon size={22} />
                        <label className="cursor-pointer">SharePoint</label>
                      </> : <>
                        <PackageIcon size={22} />
                        <label className="cursor-pointer">{remoteStorageLink?.type}</label>
                      </>}
                  </a>}
              </div>
              <button id="SyncNowButton" onClick={event => {
              handleSyncData();
              recordStat('SyncNowButtonClicked', event, statsContext, !!featureFlags?.useIntercom);
            }} disabled={isSyncing} className="flex flex-row items-center justify-end">
                <div className="flex flex-row items-center justify-center gap-2 px-2 py-1 border border-solid border-black/80 hover:border-black/40 hover:cursor-pointer rounded-full opacity-60 hover:opacity-100 bg-white/10">
                  <CloudArrowDownIcon size={22} className="w-6 h-6" data-sentry-element="CloudArrowDownIcon" data-sentry-source-file="backlog.tsx" />
                  <span>{isSyncing ? "Syncing..." : "Sync Now"}</span>
                </div>
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>;
};
Backlog.displayName = 'Backlog';
export default Backlog;